import { emptyArray, emptyObject } from 'Lib/constants/placeholder';

import {
  PRODUCT_TYPE_BOOST,
  PRODUCT_TYPE_SUPERBOOST,
  PRODUCT_TYPE_SUPERLIKE,
} from 'Store/entities/products.schema';
import type { Product } from 'Store/entities/products.types';
import { LIKE_STATUS, NOPE_STATUS, SUPERLIKE_STATUS } from 'Store/recs/constants';

import { productInfoMapping } from 'Containers/ModalManager/modals/OfferModal/constants';

import { type Action, type InsendioCard, type InsendioModal } from '../types';

export const CAMPAIGN_TYPE_MODAL = 'modal';
export const CAMPAIGN_TYPE_REC_CARD = 'rec_card';

export const TEMPLATE_TYPE_MODAL = 'offers';
export const TEMPLATE_TYPE_REC_CARD = 'merchandising_card';

export const CONFIRMATION_STATUS = 'confirmedStatus';
export const ACTIVE_STATUS = 'active';

export const SWIPE_RIGHT_GESTURE_TYPE = 'swipe_right';
export const SWIPE_LEFT_GESTURE_TYPE = 'swipe_left';
export const SWIPE_UP_GESTURE_TYPE = 'swipe_up';
export const CLICK_ACCEPT_BUTTON_GESTURE_TYPE = 'click_button_1';
export const CLICK_NOTHANKS_BUTTON_GESTURE_TYPE = 'click_button_2';

export const SWIPE_GESTURE_TO_CONFIRMATION_TYPE_MAP = {
  [SWIPE_RIGHT_GESTURE_TYPE]: LIKE_STATUS,
  [SWIPE_LEFT_GESTURE_TYPE]: NOPE_STATUS,
  [SWIPE_UP_GESTURE_TYPE]: SUPERLIKE_STATUS,
  [CLICK_ACCEPT_BUTTON_GESTURE_TYPE]: LIKE_STATUS,
};

export const MODAL_TYPE_PARTIAL_SCREEN = 'floating';
export const MODAL_TYPE_FULL_SCREEN = 'product_marketing';
export const MODAL_TYPE_FULL_SCREEN_V2 = 'full_screen_modal_v2';
export const MODAL_TYPE_PARTIAL_SCREEN_V2 = 'partial_screen_modal_v2';

export const MODAL_PRESENTATION_RULE_APP_OPEN = 'app_open';
export const MODAL_PRESENTATION_RULE_BOOST = 'boost';
export const MODAL_PRESENTATION_RULE_SUPERBOOST = 'superboost';
export const MODAL_PRESENTATION_RULE_SUPERLIKE = 'superlike';

export const OFFER_ELIGIBLE_PRODUCTS = [
  PRODUCT_TYPE_SUPERBOOST,
  PRODUCT_TYPE_BOOST,
  PRODUCT_TYPE_SUPERLIKE,
];

export function getMerchandisingCardDeeplinkUrl(
  actions: Action[],
  userGestureType?: string | null
): any {
  const selectedAction = actions.find((action) => action.gestureType === userGestureType);
  return selectedAction?.url;
}

export const getColorHash = (hash?: string) => (hash ? `#${hash}` : undefined);
export const getBackgroundImage = (url?: string) => (url ? `url(${url})` : undefined);

export const getCampaignData = (data: InsendioCard | InsendioModal) => {
  const template = data?.template || emptyObject;
  // container
  const contentBackgroundImage = template?.contentView?.backgroundImage;
  const contentBackgroundColor = template?.contentView?.backgroundColorHex;

  // hero
  const hero = template?.heroImageView ?? emptyObject;

  // body
  const header = template?.header ?? emptyObject;
  const message = template?.message ?? emptyObject;

  // cta
  const buttons = template?.buttons ?? emptyArray;

  // offer
  const offer = template?.offer;

  // product type
  const productType = offer?.info?.productType ?? '';

  // presentation rule
  const presentationRule = data?.presentation?.rule ?? '';

  // modal tyle
  const modalType = template?.name ?? '';

  return {
    backgroundColor: getColorHash(contentBackgroundColor),
    backgroundImage: getBackgroundImage(contentBackgroundImage),
    buttons,
    header,
    hero,
    message,
    modalType,
    offer,
    presentationRule,
    productType,
  };
};

export const getProductData = (products: Product[], productType: string) => {
  const productTypeIntlId = productInfoMapping[productType]?.intlId || '';
  const productName = productInfoMapping[productType]?.product;
  // list of discounted skus
  const discountedProducts: Product[] =
    products?.filter(
      (product) => productName === product.productType && !!product.originalProductId
    ) || emptyArray;

  // pick the first sku from the list (only one discount sku allowed in the modals in v1)
  const discountedProductSku = discountedProducts?.[0] || {};
  const { originalProductId } = discountedProductSku;

  // list of original products associated with the discounted sku
  const regularProducts =
    products?.filter(
      (product) =>
        product.productId === originalProductId ||
        // If originalProductId does not match any productId, fall back to the base group product
        (product.isBaseGroup && product.productType === productType)
    ) || [];

  // pick the first sku from the list
  const regularProduct = regularProducts?.[0] || {};

  return {
    discountedProducts,
    discountedProductSku,
    productTypeIntlId,
    regularProduct,
  };
};
